import axios, {
  AxiosHeaders,
  type AxiosRequestConfig,
  type RawAxiosHeaders,
  type RawAxiosRequestHeaders,
} from 'axios';

const removeAuthorization = (headers: AxiosRequestConfig['headers']): void => {
  if (!headers) return;

  // Not `AxiosHeaders.delete()`: it removes one case-insensitive match, and a direct
  // `headers.Authorization =` beside a caller's `authorization` leaves two.
  for (const name of Object.keys(headers)) {
    if (name.toLowerCase() === 'authorization') delete (headers as RawAxiosRequestHeaders)[name];
  }
};

/**
 * Strips the bearer token from a rejected request, in place, so a caller that logs or reports the
 * error cannot leak it.
 *
 * Context: a logged Axios error carries its `config`, and console captures such as Userback's synced
 * the reporter's access token into Linear that way (PLE-1728).
 */
export const withoutCredentials = <T>(reason: T): T => {
  if (axios.isAxiosError(reason)) {
    removeAuthorization(reason.config?.headers);
    removeAuthorization(reason.response?.config?.headers);
  }

  return reason;
};

/**
 * A copy of `config` that carries `token`. A replay gets a copy, never the original, because the
 * original belongs to an error its caller may already have logged.
 */
export const withBearerToken = <T extends AxiosRequestConfig>(config: T, token: string): T => ({
  ...config,
  headers: new AxiosHeaders(config.headers as RawAxiosHeaders).set(
    'Authorization',
    `Bearer ${token}`,
  ),
});
